import { useCallback, useEffect, useMemo } from 'react';

import {
  type AgentField,
  type AgentTestClientFields,
  type ClientTestFieldData,
  useGetUserAgentFieldsAgentTestFieldsValuesGetQuery,
} from '@shared/api';
import { agentTestEvents } from '@shared/services/agent-test';

import type { AgentTestFieldInfo } from '../lib/types';

type UseAgentTestFieldsParams = {
  agentId: number;
  agentFields?: AgentField[] | null;
};

const isAgentFieldsGroup = (value: unknown): value is AgentTestClientFields => {
  if (!value || typeof value !== 'object') return false;
  return Array.isArray((value as AgentTestClientFields).client_fields);
};

const resolveFieldsForAgent = (agentGroups: AgentTestClientFields[], agentId: number) => {
  return (
    agentGroups.find((group) => Number(group.agent_id) === agentId) ??
    agentGroups.find((group) => Boolean(group.current_agent)) ??
    agentGroups[0] ??
    null
  );
};

export const useAgentTestFields = ({ agentId, agentFields }: UseAgentTestFieldsParams) => {
  const {
    data: defaultFieldsValues,
    refetch: refetchDefaultFields,
  } = useGetUserAgentFieldsAgentTestFieldsValuesGetQuery(
    { agentId },
    {
      skip: !agentId,
      refetchOnMountOrArgChange: true,
    },
  );

  const resetFieldValues = useCallback(() => {
    if (!agentId) return;
    void refetchDefaultFields();
  }, [agentId, refetchDefaultFields]);

  const fieldsByAgents = useMemo(() => {
    return (defaultFieldsValues ?? []).filter(isAgentFieldsGroup);
  }, [defaultFieldsValues]);

  const activeAgentFields = useMemo<ClientTestFieldData[]>(() => {
    const activeAgent = resolveFieldsForAgent(fieldsByAgents, agentId);
    return activeAgent?.client_fields ?? [];
  }, [agentId, fieldsByAgents]);

  const fieldValuesById = useMemo<Record<number, string>>(() => {
    const next: Record<number, string> = {};

    activeAgentFields.forEach((field) => {
      next[field.agent_field_id] = field.value ?? '';
    });

    return next;
  }, [activeAgentFields]);

  useEffect(() => {
    if (!agentId) return;

    const unsubscribe = agentTestEvents.onFieldsChanged((payload) => {
      const payloadAgentId = Number(payload?.agent_id);
      if (Number.isFinite(payloadAgentId) && payloadAgentId !== agentId) return;

      void refetchDefaultFields();
    });

    return () => {
      unsubscribe();
    };
  }, [agentId, refetchDefaultFields]);

  const mappedFields = useMemo<AgentTestFieldInfo[]>(() => {
    const sourceFields = agentFields ?? [];

    if (!sourceFields.length) {
      return activeAgentFields.map((field) => ({
        id: field.agent_field_id,
        label: field.agent_field_name,
        value: field.value ?? '',
        isRequired: Boolean(field.is_required),
      }));
    }

    return sourceFields.map((field) => ({
      id: field.id,
      label: field.name,
      value: fieldValuesById[field.id] ?? '',
      isRequired: Boolean(field.is_required),
    }));
  }, [activeAgentFields, agentFields, fieldValuesById]);

  const leadFields = useMemo(
    () => mappedFields.filter((field) => field.isRequired),
    [mappedFields],
  );

  const clientFields = useMemo(
    () => mappedFields.filter((field) => !field.isRequired),
    [mappedFields],
  );

  const isLeadCreated = useMemo(() => {
    if (!leadFields.length) return false;
    return leadFields.every((field) => Boolean(field.value.trim()));
  }, [leadFields]);

  return {
    leadFields,
    clientFields,
    isLeadCreated,
    resetFieldValues,
  };
};
